// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Bill cost estimate content renderer.
 * Used by BillRendererDialog to display summary + line items table.
 */
import { type FC, useCallback, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Check, Copy } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import type { BillCostEstimate } from '../../models/types'

function formatCurrency(amount: number): string {
  const abs = Math.abs(amount)
  const formatted = abs.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
  return amount < 0 ? `(${formatted})` : formatted
}

/** Format cost estimate as plain text for clipboard */
function formatAsPlainText(estimate: BillCostEstimate): string {
  const lines: string[] = []

  if (estimate.summary) {
    lines.push('Summary', estimate.summary, '')
  }

  if (estimate.lineItems.length > 0) {
    lines.push('Line Items')
    for (const item of estimate.lineItems) {
      lines.push(`  ${item.description}: ${formatCurrency(item.estimatedCost)}`)
      lines.push(`    Assumptions: ${item.assumptions}`)
    }
    lines.push('')
  }

  lines.push(`Total: ${formatCurrency(estimate.totalEstimatedCost)}`)

  return lines.join('\n').trimEnd()
}

/** Content renderer for bill-cost-estimate component type */
export const BillCostEstimateContent: FC<{ data: unknown }> = ({ data }) => {
  const costEstimate = data as BillCostEstimate
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    if (!costEstimate) return
    void navigator.clipboard.writeText(formatAsPlainText(costEstimate)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [costEstimate])

  if (!costEstimate?.lineItems) return null

  return (
    <div className="space-y-6 py-4">
      {/* Copy button */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {costEstimate.summary}
          </p>
        </CardContent>
      </Card>

      {/* Line Items */}
      {costEstimate.lineItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Line Items</CardTitle>
          </CardHeader>
          <CardContent>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">Description</TableHead>
                  <TableHead className="text-right w-[15%]">Estimated Cost</TableHead>
                  <TableHead className="w-[50%]">Assumptions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costEstimate.lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium whitespace-normal break-words align-top">{item.description}</TableCell>
                    <TableCell className={`text-right font-mono whitespace-nowrap align-top ${item.estimatedCost < 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                      {formatCurrency(item.estimatedCost)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-normal break-words align-top">{item.assumptions}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-bold">Total</TableCell>
                  <TableCell className={`text-right font-mono font-bold whitespace-nowrap ${costEstimate.totalEstimatedCost < 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                    {formatCurrency(costEstimate.totalEstimatedCost)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
